<template>
  <div class="geofence-edit p-0 m-0 p-relative h-100">
    <div id="geofence-map" class="w-100" :style="'height: ' + mapHeight()"></div>
  </div>
</template>

<script>
import MapService from '@/services/MapService'
import mapLayersConfig from '@/mixins/mapLayersConfig.mixin'
import GeofenceAutocomplete from '@/components/Autocompletes/GeofenceAutocomplete'
import GeolineAutocomplete from '@/components/Autocompletes/GeolineAutocomplete'
import GeopointAutocomplete from '@/components/Autocompletes/GeopointAutocomplete'
import GeofenceMapPopupEditor from '@/components/MapsV2/Popups/MapPopupEditor'
import TilesLayerControl from '@/components/MapsV2/Controls/TilesLayerControl'
import GeoTypesControl from '@/components/MapsV2/Controls/GeoTypesControl'
import L from 'leaflet'
import { GEOMETRY_ERRORS, validateGeofencePolygon, ensureWinding } from '@/common/utils/geometryValidation'

export default {
  name: 'GeofencesEditPage',
  mixins: [mapLayersConfig],
  components: {
    GeofenceAutocomplete,
    GeolineAutocomplete,
    GeopointAutocomplete,
    GeofenceMapPopupEditor,
    TilesLayerControl,
    GeoTypesControl
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mapReady: false,
      layersVisibility: {
        geofences_self: true,
        geopoints_self: true,
        geolines_self: true
      },
      counters: {
        geofences_self: 0,
        geopoints_self: 0,
        geolines_self: 0
      },
      editingFeature: null,
      activeFetches: {}
    }
  },
  computed: {
    disableStuff() {
      return !!this.editingFeature
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.mapHeight()
        this.$nextTick(() => {
          this.handleResize()
          MapService.invalidateSize()
        })
      }
    }
  },
  async mounted() {
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial

    await this.checkMapProviders()
    this.center = this.frontConfig?.maps?.general?.lat ? { lat: this.frontConfig.maps.general.lat, lng: this.frontConfig.maps.general.lng } : { lat: 0, lng: 0 }
    this.initMap()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    MapService.destroy()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async initMap() {
      // Create an isolated version of the layers config for this page
      const isolatedConfig = { ...this.mapLayersConfig }
      isolatedConfig.geofences_self = this.getGeofencesEditConfig()
      isolatedConfig.geopoints_self = this.getGeoPointsEditConfig()
      isolatedConfig.geolines_self = this.getGeoLinesEditConfig()

      MapService.init('geofence-map', {
        minZoom: 11,
        center: this.center,
        zoom: 13,
        editable: true // CRITICAL for Leaflet.Editable
      }, this.$store, this, isolatedConfig)

      // Setup Tile Providers
      await this.checkMapProviders()
      MapService.setTileProviders(this.tileProviders)

      if (this.tileProviders) {
        this.tileProvidersStatic = [...new Set(this.tileProviders)]
        this.setDefaultTileProvider('GeofenceGeneralMap')
        MapService.setTileProviders(this.tileProvidersStatic)
      }

      this.mapReady = true

      MapService.addControl(
        GeoTypesControl,
        null,
        'bottomleft',
        this.$store,
        this,
        {
          'filters-changed': (filters) => {
            this.filters = filters
            this.refreshLayers()
          },
          'center-on': (item) => {
            this.centerOn(item)
          }
        }
      )

      MapService.addScaleControl({
        position: 'bottomright',
        metric: this.metric,
        imperial: this.imperial
      })

      MapService.addControl(
        TilesLayerControl,
        null,
        'topright'
      )

      // Initial Load
      this.refreshLayers()

      // Bind Map Events
      MapService.on('moveend', () => {
        this.refreshLayers()
      })

      MapService.on('editable:drawing:finished', (e) => {
        console.log('Drawing finished:', e)
        this.editingFeature = { layer: e.layer, isNew: true }
      })
    },
    setDefaultTileProvider(mapName = this.$options.name) {
      if (this.ecosystemMaps?.defaultTileProvider?.[mapName]) {
        this.tileProvidersStatic = this.tileProvidersStatic.map(x => {
          if (x.name === this.ecosystemMaps?.defaultTileProvider?.[mapName]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
      }
    },
    refreshLayers() {
      if (this.editingFeature) return // DO NOT REFRESH while editing to avoid losing references/ghost layers
      Object.keys(this.layersVisibility).forEach(key => {
        if (this.layersVisibility[key]) {
          this.fetchLayer(key)
        }
      })
    },
    async fetchLayer(layerKey) {
      let configSource = this.mapLayersConfig[layerKey]

      // ISOLATION: For editing geofences, we use a custom local config
      if (layerKey === 'geofences_self') {
        configSource = this.getGeofencesEditConfig()
      } else if (layerKey === 'geopoints_self') {
        configSource = this.getGeoPointsEditConfig()
      } else if (layerKey === 'geolines_self') {
        configSource = this.getGeoLinesEditConfig()
      }

      if (!configSource) return

      // Avoid parallel identical fetches
      if (this.activeFetches[layerKey]) return
      this.$set(this.activeFetches, layerKey, true)

      try {
        const config = { ...configSource }

        // Inject listeners for interactive popups
        if (layerKey === 'geofences_self' || layerKey === 'geopoints_self' || layerKey === 'geolines_self') {
          config.listeners = {
            'start-edit': (payload) => this.handleStartEdit(payload),
            'save-edit': (payload) => this.handleSaveEdit(payload),
            'cancel-edit': (payload) => this.handleCancelEdit(payload),
            delete: (payload) => this.handleDelete(payload)
          }
        }

        const params = typeof config.params === 'function' ? config.params() : (config.params || {})
        if (!params) return

        const response = await this.$http.get(config.endpoint(this.$appConfig), { params })
        const data = config.transform ? config.transform(response.body) : response.body

        this.$set(this.counters, layerKey, data.length)

        if (config.customUpdate) {
          config.customUpdate(data, config, layerKey)
        } else {
          MapService.updateMarkers(layerKey, data, config)
        }
      } catch (e) {
        console.error(`Error fetching layer ${layerKey}:`, e)
      } finally {
        this.$set(this.activeFetches, layerKey, false)
      }
    },
    getGeofencesEditConfig() {
      // Return the specific configuration for this page only
      const base = this.mapLayersConfig.geofences_self
      return {
        ...base,
        params: () => {
          const bounds = MapService.getBounds()
          if (!bounds) return null
          if (!this.filters?.polygonTypes?.length) {
            MapService.toggleLayer('geofences_self', false)
            this.$set(this.counters, 'geofences_self', 0)
            return null
          }
          MapService.toggleLayer('geofences_self', true)
          return {
            filter_groups: [{
              filters: [
                ['boundary', 'eq', bounds._southWest.lat + ',' + bounds._southWest.lng + '|' + bounds._northEast.lat + ',' + bounds._northEast.lng],
                ['geo_type_id', 'in', this.filters.polygonTypes]
              ]
            }],
            fields: 'id,external_id,name,fence,geo_type_id,geo_type.level,geo_type.name,geo_type.type,lat,lng,reference_location',
            includes: ['geo_type']
          }
        },
        transform: (response) => {
          const data = response.data || response
          if (!Array.isArray(data)) return null
          console.log('[Page] Transforming geofences data, count:', data.length)
          return data
            .filter(item => item.fence)
            .map(item => {
              let fence = item.fence
              if (typeof fence === 'string') {
                try {
                  fence = JSON.parse(fence)
                } catch (e) {
                  return null
                }
              }
              if (!fence || !fence.type) return null

              // Standard properties object
              const properties = {
                ...(fence.properties || {}),
                id: item.id,
                name: item.name,
                external_id: item.external_id,
                geo_type: item.geo_type,
                reference_location: item.reference_location,
                color: this.stringToColour('11' + (item.geo_type?.name || 'default'))
              }

              return {
                type: 'Feature',
                geometry: fence.type === 'Feature' ? fence.geometry : fence,
                properties
              }
            }).filter(Boolean)
        },
        style: (feature) => {
          const color = feature.properties?.color || '#3388ff'
          return {
            fillColor: color,
            weight: 2,
            opacity: 1,
            color,
            fillOpacity: 0.3
          }
        },
        markerPopup: GeofenceMapPopupEditor
      }
    },
    getGeoPointsEditConfig() {
      return {
        label: this.$gettext('GeoPoints'),
        visible: true,
        endpoint: (appConfig) => `${appConfig.api.baseUrl}geo-points`,
        params: () => {
          const bounds = MapService.getBounds()
          if (!bounds) return null
          if (!this.filters?.pointTypes?.length) {
            MapService.toggleLayer('geopoints_self', false)
            this.$set(this.counters, 'geopoints_self', 0)
            return null
          }
          MapService.toggleLayer('geopoints_self', true)
          return {
            filter_groups: [{
              filters: [
                ['boundary', 'eq', bounds._southWest.lat + ',' + bounds._southWest.lng + '|' + bounds._northEast.lat + ',' + bounds._northEast.lng],
                ['geo_type_id', 'in', this.filters.pointTypes]
              ]
            }],
            includes: ['geo_type']
          }
        },
        transform: (response) => {
          const data = response.data || response
          if (!Array.isArray(data)) return []
          return data.map(item => {
            // Style based on geo_type_id: 6 = Inbound (Green), 7 = Outbound (Red)
            const isEntry = item.geo_type_id === 6
            const themeColor = isEntry ? '#64d000' : '#ff4d4d'

            return {
              ...item,
              id: item.id,
              lat: Number.parseFloat(item.lat),
              lng: Number.parseFloat(item.lng),
              label: item.name || item.external_id,
              themeColor,
              isEntry
            }
          })
        },
        markerRenderer: (item) => {
          const { themeColor, isEntry } = item.item
          return {
            html: `
              <div class="geopoint-marker ${isEntry ? 'is-entry' : 'is-exit'}" style="--point-color: ${themeColor}">
                <div class="marker-pulse"></div>
                <div class="marker-core">
                  <i class="fas ${isEntry ? 'fa-sign-in-alt' : 'fa-sign-out-alt'}" style="font-size: 10px;"></i>
                </div>
              </div>
            `,
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          }
        },
        markerTooltip: (item) => `
          <div class="geopoint-tooltip">
            <strong>${this.$options.filters.cleanentityprefix(item.name || item.external_id)}</strong>
            <div class="small">${item.geo_type?.name || (item.geo_type_id === 6 ? 'Entry' : 'Exit')}</div>
          </div>
        `,
        markerPopup: GeofenceMapPopupEditor
      }
    },
    getGeoLinesEditConfig() {
      return {
        label: this.$gettext('GeoLines'),
        visible: true,
        layerParams: {
          type: 'geoJSON',
          pane: 'geolinesPane',
          zIndex: 360
        },
        endpoint: (appConfig) => `${appConfig.api.baseUrl}geo-lines`,
        params: () => {
          const bounds = MapService.getBounds()
          if (!bounds) return null
          if (!this.filters?.lineTypes?.length) {
            MapService.toggleLayer('geolines_self', false)
            this.$set(this.counters, 'geolines_self', 0)
            return null
          }
          MapService.toggleLayer('geolines_self', true)
          return {
            filter_groups: [{
              filters: [
                ['boundary', 'eq', bounds._southWest.lat + ',' + bounds._southWest.lng + '|' + bounds._northEast.lat + ',' + bounds._northEast.lng],
                ['geo_type_id', 'in', this.filters.lineTypes]
              ]
            }],
            includes: ['geo_type']
          }
        },
        transform: (response) => {
          const data = response.data || response
          if (!Array.isArray(data)) return null
          return data
            .filter(item => item.path || item.fence)
            .map(item => {
              let geometry = item.path || item.fence
              if (typeof geometry === 'string') {
                try { geometry = JSON.parse(geometry) } catch (e) { return null }
              }
              if (!geometry || !geometry.type) return null

              const isEntry = item.geo_type_id === 5
              const themeColor = isEntry ? '#64d000' : '#ff4d4d'

              const properties = {
                ...(geometry.properties || {}),
                id: item.id,
                name: item.name,
                external_id: item.external_id,
                geo_type: item.geo_type,
                color: themeColor
              }

              return {
                type: 'Feature',
                geometry: geometry.type === 'Feature' ? geometry.geometry : geometry,
                properties
              }
            }).filter(Boolean)
        },
        style: (feature) => {
          const color = feature.properties?.color || '#3388ff'
          return {
            color,
            weight: 3,
            opacity: 0.9,
            dashArray: '5, 5'
          }
        },
        markerTooltip: (props) => `<strong>${this.$options.filters.cleanentityprefix(props.name || props.external_id)}</strong><br><small>${props.geo_type?.name || ''}</small>`,
        markerPopup: GeofenceMapPopupEditor,
        customUpdate: (data, config, layerKey) => {
          MapService.updateGeofences(layerKey, data, config)
        }
      }
    },
    handleStartEdit({ item, layer }) {
      console.log('[Page] handleStartEdit called with properties:', JSON.stringify(item))
      console.log('[Page] Starting edit for:', item.name || item.external_id || 'unnamed')
      // Store original coordinates in case of cancel
      if (layer.getLatLngs) {
        layer._originalLatLngs = JSON.parse(JSON.stringify(layer.getLatLngs()))
      } else if (layer.getLatLng) {
        layer._originalLatLng = layer.getLatLng()
      }
      MapService.toggleEdit(layer, true)
      this.editingFeature = { item, layer, isNew: false }
    },
    geometryErrorMessage(code) {
      // Same copy as GeofenceForm.vue (v1 editor) for consistency.
      const messages = {
        [GEOMETRY_ERRORS.SELF_INTERSECTION]: this.$gettext('The polygon has crossing edges. Fix the self-intersection before saving.'),
        [GEOMETRY_ERRORS.TOO_FEW_VERTICES]: this.$gettext('A polygon needs at least 3 distinct vertices.'),
        [GEOMETRY_ERRORS.ZERO_AREA]: this.$gettext('The polygon has no area: all vertices are aligned in a straight line.')
      }
      return messages[code] || code
    },
    async handleSaveEdit({ item, layer }) {
      console.log('Saving edit for:', item.name || item.external_id)

      const id = item.id
      if (!id) {
        console.error('Missing ID for element update')
        return
      }

      let endpoint = ''
      let payload = {}

      try {
        if (layer instanceof L.Polygon) {
          // Geofence
          endpoint = `geo-fences/${id}`
          const rings = layer.getLatLngs()
          const outerRing = Array.isArray(rings[0]) ? rings[0] : rings

          const fence = outerRing.map(ll => [ll.lat, ll.lng])
          // Block invalid geometry from reaching the backend; keep the polygon in
          // edit mode so the operator can fix it (do not call disableEdit here).
          const errors = validateGeofencePolygon(fence)
          if (errors.length > 0) {
            this.$root.$children[0].$refs.simplert.openSimplert({
              title: this.$gettext('Invalid geometry'),
              message: errors.map(code => this.geometryErrorMessage(code)).join('\n'),
              type: 'error'
            })
            return
          }

          payload = {
            // Auto-correct winding to CCW (exterior ring, GeoJSON / RFC 7946).
            fence: ensureWinding(fence, false),
            coordinates: [outerRing.map(ll => ({ lat: ll.lat, lng: ll.lng }))]
          }
        } else if (layer instanceof L.Polyline) {
          // GeoLine (Polyline but NOT Polygon)
          endpoint = `geo-lines/${id}`
          const latLngs = layer.getLatLngs()
          payload = {
            path: latLngs.map(ll => [ll.lat, ll.lng]),
            coordinates: latLngs.map(ll => ({ lat: ll.lat, lng: ll.lng }))
          }
        } else if (layer instanceof L.Marker) {
          // GeoPoint
          endpoint = `geo-points/${id}`
          const pos = layer.getLatLng()
          payload = {
            name: item.name,
            lat: pos.lat,
            lng: pos.lng
          }
        }

        if (endpoint) {
          const finalUrl = `${this.$appConfig.api.baseUrl}${endpoint}`
          await this.$http.patch(finalUrl, payload)

          // this.$root.$children[0].$refs.simplert.openSimplert({
          //   title: this.$gettext('Success'),
          //   message: this.$gettext('Element updated successfully'),
          //   type: 'success'
          // })

          // Refresh the relevant layers
          this.refreshLayers()
        }
      } catch (error) {
        console.error('Error saving edit:', error)
        this.$root.$children[0].$refs.simplert.openSimplert({
          title: this.$gettext('Error'),
          message: this.$gettext('Failed to update element'),
          type: 'error'
        })
      }

      this.disableEdit(layer)
    },
    async handleDelete({ item, layer }) {
      console.log('Deleting:', item.name || item.external_id)

      const id = item.id
      if (!id) {
        console.error('Missing ID for element delete')
        return
      }

      let endpoint = ''

      try {
        if (layer instanceof L.Polygon) {
          // Geofence
          endpoint = `geo-fences/${id}`
        } else if (layer instanceof L.Polyline) {
          // GeoLine (Polyline but NOT Polygon)
          endpoint = `geo-lines/${id}`
        } else if (layer instanceof L.Marker) {
          // GeoPoint
          endpoint = `geo-points/${id}`
        }

        if (endpoint) {
          const finalUrl = `${this.$appConfig.api.baseUrl}${endpoint}`
          await this.$http.delete(finalUrl)

          // Refresh the relevant layers
          this.refreshLayers()
        }
      } catch (error) {
        console.error('Error deleting:', error)
        this.$root.$children[0].$refs.simplert.openSimplert({
          title: this.$gettext('Error'),
          message: this.$gettext('Failed to delete element'),
          type: 'error'
        })
      }

      this.disableEdit(layer)
    },
    handleCancelEdit({ item, layer }) {
      console.log('[Page] Cancelling edit for:', item.name || item.external_id)

      // Close popup to avoid stale state
      if (layer.closePopup) layer.closePopup()

      // 1. Restore original coordinates BEFORE disabling edit
      if (layer._originalLatLngs) {
        if (typeof layer.setLatLngs === 'function') {
          layer.setLatLngs(layer._originalLatLngs)
          if (layer.redraw) layer.redraw()
        }
        delete layer._originalLatLngs
      } else if (layer._originalLatLng) {
        if (typeof layer.setLatLng === 'function') {
          layer.setLatLng(layer._originalLatLng)
        }
        delete layer._originalLatLng
      }

      this.disableEdit(layer)
    },
    disableEdit(layer) {
      if (layer) {
        MapService.toggleEdit(layer, false)
      }
      this.editingFeature = null
    },
    toggleLayer(key) {
      MapService.toggleLayer(key, this.layersVisibility[key])
      if (this.layersVisibility[key]) {
        this.fetchLayer(key)
      }
    },
    centerOn(element) {
      if (!element) return
      if (element.lat && element.lng) {
        MapService.setCenter([element.lat, element.lng], 16)
      } else if (element.fence || element.path) {
        // Simplified handling for fitBounds if it's a geofence
        const geoJsonLayer = L.geoJSON(element.fence || element.path)
        MapService.fitBounds(geoJsonLayer.getBounds())
      }
    },
    startDrawing(type) {
      if (type === 'polygon') {
        MapService.startPolygon()
      }
    },
    stopDrawing() {
      MapService.stopDrawing()
      if (this.editingFeature && this.editingFeature.isNew) {
        this.editingFeature.layer.remove()
        this.editingFeature = null
      }
    },
    saveEdit() {
      if (this.editingFeature) {
        const { item, layer } = this.editingFeature
        this.handleSaveEdit({ item, layer })
      }
    },
    cancelEdit() {
      if (this.editingFeature) {
        const { layer, isNew, item } = this.editingFeature
        if (isNew) {
          layer.remove()
          this.editingFeature = null
        } else {
          this.handleCancelEdit({ item, layer })
        }
      }
    },
    handleResize() {
      if (this.mapReady) {
        MapService.invalidateSize()
      }
    },
    mapHeight() {
      if (document.getElementById('geofence-map')) {
        const mapPosition = document.getElementById('geofence-map').getBoundingClientRect().top
        const windowScroll = window.scrollY
        const calc = windowScroll + mapPosition + 24
        document.getElementById('geofence-map').style.height = 'calc(100vh - ' + calc + 'px)'
        return 'calc(100vh - ' + calc + 'px)'
      }
    }
  }
}
</script>

<style>
.gap-2 {
  gap: 0.5rem;
}

.map-controls-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
}

.bg-glass {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.editing-toolbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  background: rgba(0, 0, 0, 0.8);
  padding: 10px 20px;
  border-radius: 50px;
}

/* GeoPoint Neon Styles - NOT SCOPED for Leaflet */
.geopoint-marker {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.marker-core {
  position: relative;
  width: 16px;
  height: 16px;
  background: var(--point-color);
  border-radius: 50%;
  border: 1.5px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 0 8px var(--point-color), 0 0 3px rgba(0,0,0,0.3);
  z-index: 2;
}

.marker-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  background: var(--point-color);
  border-radius: 50%;
  opacity: 0.3;
  animation: marker-pulse-anim 3s infinite ease-out;
  z-index: 1;
}

@keyframes marker-pulse-anim {
  0% {
    transform: scale(0.6);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.geopoint-tooltip {
  padding: 4px 8px;
  border-left: 4px solid var(--point-color);
  font-weight: 600;
}
.geofences-edit-page .leaflet-popup-content {
  width: auto !important;
}
</style>
